'use client';

import { CSSProperties, RefObject, useRef } from 'react';
import {
	Column,
	getCoreRowModel,
	Table,
	useReactTable,
} from '@tanstack/react-table';
import { createColumnStyleGetter } from './utils';
import { PINNED_COLUMN_STUCK_CLASSES } from './consts';
import { useHasHorizontalOverflow } from './useHasHorizontalOverflow';
import { useScrollPosition } from './useScrollPosition';
import { UseDataTableSetupOptions } from 'src/components/primitives/Table/types';

interface UseDataTableSetupReturn<T> {
	table: Table<T>;
	containerRef: RefObject<HTMLDivElement | null>;
	getColumnStyle: (column: Column<T>) => CSSProperties;
	getStuckClass: (isPinned: 'left' | 'right' | false) => string | undefined;
	showPaginator: boolean;
	isEmpty: boolean;
}

export function useDataTableSetup<T>({
	data,
	columns,
	meta,
	columnPinning,
	pagination,
}: UseDataTableSetupOptions<T>): UseDataTableSetupReturn<T> {
	const containerRef = useRef<HTMLDivElement>(null);
	const hasOverflow = useHasHorizontalOverflow(containerRef);
	const isScrolledEnd = useScrollPosition(containerRef, {
		direction: 'horizontal',
		position: 'end',
	});

	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		meta,
		initialState: columnPinning ? { columnPinning } : undefined,
	});

	const getColumnStyle = createColumnStyleGetter(table);

	const showPaginator = !!(
		pagination && pagination.totalCount > pagination.pageSize
	);

	const getStuckClass = (isPinned: 'left' | 'right' | false) => {
		if (!isPinned || !hasOverflow) return undefined;
		if (isPinned === 'right' && isScrolledEnd) return undefined;
		return PINNED_COLUMN_STUCK_CLASSES[isPinned];
	};

	const isEmpty = table.getRowModel().rows.length === 0;

	return {
		table,
		containerRef,
		getColumnStyle,
		getStuckClass,
		showPaginator,
		isEmpty,
	};
}
